import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import gql from 'graphql-tag';
import { themes } from 'kb-shared';
import moment from 'moment';
import { useQuery } from 'react-apollo';
import Loader from '../../../components/Loader/Loader';
import PatientFields from './PatientFields';
import ContactInfoFields from './ContactInfoFields';
import PharmacyFields from './PharmacyFields';
import TrusteesFields from './TrusteesFields';
import { InputsLabel, Icon, Text } from './shared';
import { trustee } from '../../../assets/icons';
import InsuranceDetails from 'kb-components/Screens/MyProfile/InsuranceDetails';
import InsuranceCardUploadModal from 'components/PhotoUpload/InsuranceCard/InsuranceCardUploadModal';

export const patientQuery = gql`
  query myProfile {
    patient {
      id
      firstName
      lastName
      pronoun
      gender
      birthday
      email
      phone
      address {
        id
        address1
        address2
        city
        state
        zipcode
        country
      }
      patientPharmacy {
        id
        name
        phone
      }
      patientTrustees {
        id
        name
        phone
        state
        relationship
      }
      preferredTimezone
    }
  }
`;

interface PatientTrustee {
  id: string;
  name: string;
  phone: string;
  relationship: string;
  state: string;
}
interface PatientResponse {
  patient: {
    id: string;
    firstName: string;
    lastName: string;
    pronoun: string;
    gender: string;
    birthday: string;
    email: string;
    phone: string;
    address?: {
      id: string;
      address1?: string;
      address2?: string;
      city?: string;
      state?: string;
      zipcode?: string;
      country?: string;
    };
    patientPharmacy?: {
      id: string;
      name?: string;
      phone?: string;
    };
    patientTrustees?: PatientTrustee[];
    preferredTimezone?: string;
  };
}

function ProfileForm() {
  const result = useQuery<PatientResponse>(patientQuery);
  const [showingModal, setShowingModal] = useState(false);
  const body = document.body;
  if (!body) return null;
  const { data, loading } = result;
  if (loading) {
    return <Loader color={themes.colors.KBYellow} height={200} width={200} />;
  }

  const patient = data?.patient;
  const birthdate = patient?.birthday ? moment(patient.birthday).format('MM/DD/YYYY') : '';

  const patientTrustees: Array<PatientTrustee | null> = data?.patient.patientTrustees ?? [];
  const formatedTrustees = patientTrustees.concat([null, null, null]).slice(0, 3);

  return (
    <Form>
      <PatientFields
        firstName={patient?.firstName}
        lastName={patient?.lastName}
        pronoun={patient?.pronoun}
        gender={patient?.gender}
        birthday={birthdate}
      />
      <SectionHeader>Contact Information</SectionHeader>
      <ContactInfoFields
        address1={patient?.address?.address1}
        address2={patient?.address?.address2}
        city={patient?.address?.city}
        state={patient?.address?.state}
        zipcode={patient?.address?.zipcode}
        email={patient?.email}
        phone={patient?.phone}
        country={patient?.address?.country}
        preferredTimezone={patient?.preferredTimezone}
      />
      <SectionHeader>Preferred pharmacy</SectionHeader>
      <PharmacyFields
        pharmacyName={patient?.patientPharmacy?.name}
        phone={patient?.patientPharmacy?.phone}
      />
      <Wrapper>
        <InsuranceWrapper>
          <InsuranceDetails
            onUploadPress={() => {
              setShowingModal(true);
            }}
          />
        </InsuranceWrapper>
      </Wrapper>
      {showingModal &&
        ReactDOM.createPortal(
          <InsuranceCardUploadModal
            onClose={() => {
              setShowingModal(false);
            }}
          />,
          body
        )}
      <SectionHeader>Share my information</SectionHeader>
      <InputsLabel>
        <Icon src={trustee} />
        <Text>Allow others to view your health information</Text>
      </InputsLabel>

      <DescriptionText>
        I do hereby assign the following persons as trustees of my personal health information and I
        acknowledge that they may have access my medical information at any time:
      </DescriptionText>

      {formatedTrustees.map((trustee, idx: number) => (
        <TrusteesFields
          id={trustee?.id}
          name={trustee?.name}
          relationship={trustee?.relationship}
          phone={trustee?.phone}
          state={trustee?.state}
          key={trustee?.id ?? `tmp-${idx}`}
          position={idx + 1}
        />
      ))}
    </Form>
  );
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const SectionHeader = styled.h2`
  ${({ theme }) => theme.fonts.DomaineDisplay};
  color: ${themes.colors.KBNight};
  font-size: 36px;
  margin: 25px 0px;
`;

const DescriptionText = styled.p`
  ${({ theme }) => theme.fonts.FoundersGrotesk};
  color: ${({ theme }) => theme.colors.KBNight};
  white-space: pre-wrap;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;
const InsuranceWrapper = styled.div`
  padding: 24px 0;
  max-width: 380px;
`;

export default ProfileForm;
